// Mirror of backend app/services/workflow_llm_resolver.py.
// KEEP IN SYNC — see RESEARCH P-10 (contract test in Phase 5 will verify parity).
// HARDCODED_FALLBACK values MUST match the backend's _HARDCODED_FALLBACK exactly.

import type { LLMProviderValue } from '@/schemas/llm-api-key'

export interface EffectiveLLMConfig {
  provider: LLMProviderValue
  model: string
  temperature: number
  max_tokens: number
  top_p: number | null
  frequency_penalty: number | null
  presence_penalty: number | null
  stop: string[] | null
  seed: number | null
  api_key_id: string | null
  advanced: Record<string, unknown>
  model_specific: Record<string, unknown>
}

const HARDCODED_FALLBACK: EffectiveLLMConfig = {
  provider: 'openai',
  model: 'gpt-4o-mini',
  temperature: 0.7,
  max_tokens: 2048,
  top_p: null,
  frequency_penalty: null,
  presence_penalty: null,
  stop: null,
  seed: null,
  api_key_id: null,
  advanced: {},
  model_specific: {},
}

const INHERITABLE_FIELDS = [
  'provider',
  'model',
  'temperature',
  'max_tokens',
  'top_p',
  'frequency_penalty',
  'presence_penalty',
  'stop',
  'seed',
] as const

type InheritableField = (typeof INHERITABLE_FIELDS)[number]

function resolveField(
  field: InheritableField,
  nodeConfig: Record<string, unknown>,
  workflowDefault: Record<string, unknown>
): unknown {
  const entry = nodeConfig[field]
  // Wrapped shape: {inherited: bool, value: T | null}.
  // Treat a missing/None entry as "inherited" so the workflow default applies,
  // matching backend resolver behaviour (workflow_llm_resolver.py::_resolve_field).
  if (
    entry !== null &&
    typeof entry === 'object' &&
    'inherited' in entry &&
    (entry as { inherited: boolean }).inherited === false
  ) {
    const value = (entry as { value?: unknown }).value
    return value ?? HARDCODED_FALLBACK[field]
  }
  if (field in workflowDefault) {
    return workflowDefault[field]
  }
  return HARDCODED_FALLBACK[field]
}

export function resolveEffectiveLlmConfig(
  nodeConfig: Record<string, unknown>,
  workflowSettings: Record<string, unknown>
): EffectiveLLMConfig {
  const workflowDefault =
    (workflowSettings.default_llm as Record<string, unknown> | undefined) ?? {}
  const resolved = {} as Record<string, unknown>
  for (const field of INHERITABLE_FIELDS) {
    resolved[field] = resolveField(field, nodeConfig, workflowDefault)
  }
  resolved.api_key_id =
    (nodeConfig.api_key_id as string | null | undefined) ?? null
  resolved.advanced =
    (nodeConfig.advanced as Record<string, unknown> | undefined) ?? {}
  resolved.model_specific =
    (nodeConfig.model_specific as Record<string, unknown> | undefined) ?? {}
  return resolved as unknown as EffectiveLLMConfig
}
